import { EntityItem, SearchQuery } from '@texas/components/agent/types';
import { httpClient } from '../httpClient/httpClient';
import { BranchIdentifier } from '@texas/utils/helpers/texas/branchHelpers';
import { Currency } from './currenciesApi';

export interface Branch {
  identifier: BranchIdentifier;
  id: number;
  name: string;
  displayOrder: number;
  defaultCurrencyId: number;
  defaultVatCodeId?: number;
  enableErpBatching?: boolean;
  mainInternalSupplierId?: number;
  mainInternalSupplierName?: string;
  defaultDecimalsInPrice?: number;
}

export interface CreateBranchRequest {
  identifier: string;
  name: string;
  displayOrder: number;
}

export interface UpdateBranchRequest {
  name: string;
  displayOrder: number;
  defaultCurrencyId: number;
  defaultVatCodeId?: number;
  enableErpBatching: boolean;
  mainInternalSupplierId?: number;
  defaultDecimalsInPrice?: number;
}

export interface PaymentTerm {
  id: number;
  name: string;
  identifier: string;
  branchId: number;
  displayOrder: number;
  archived: boolean;
}

export interface DeliveryTerm {
  id: number;
  name: string;
  identifier: string;
  branchId: number;
  displayOrder: number;
  archived: boolean;
}

export interface DeliveryMethod {
  id: number;
  name: string;
  identifier: string;
  branchId: number;
  displayOrder: number;
  archived: boolean;
}

export type BranchTermKind =
  | 'deliveryMethods'
  | 'deliveryTerms'
  | 'paymentTerms';

export interface BranchTermRequest {
  name: string;
  identifier: string;
  displayOrder: number;
  archived: boolean;
}

const branchTermEndpoints: Record<BranchTermKind, string> = {
  deliveryMethods: 'deliverymethods',
  deliveryTerms: 'deliveryterms',
  paymentTerms: 'paymentterms',
};

export const branchesApi = {
  getBranches: () => httpClient.get<Branch[]>(`organizations/3/branches`),
  getBranchesForOrganization: (organizationId: number) =>
    httpClient.get<Branch[]>(`organizations/${organizationId}/branches`),
  filterBranchesForChat: (query: SearchQuery) =>
    httpClient.post<EntityItem[]>(`branches/filter`, {
      data: query,
    }),
  getAvailableBranchesForCreation: () =>
    httpClient.get<string[]>(`branches/available`),
  createBranch: (organizationId: number, request: CreateBranchRequest) =>
    httpClient.post<Branch>(`organizations/${organizationId}/branches`, {
      data: request,
    }),
  updateBranch: (id: number, request: UpdateBranchRequest) =>
    httpClient.put<Branch>(`branches/${id}`, { data: request }),
  getSuppliersForBranch: (branchId: number) =>
    httpClient.get<BranchSupplier[]>(`branches/${branchId}/suppliers`),
  getPaymentTermsForBranch: (branchId: number) =>
    httpClient.get<PaymentTerm[]>(`branches/${branchId}/paymentterms`),
  getDeliveryTermsForBranch: (branchId: number) =>
    httpClient.get<DeliveryTerm[]>(`branches/${branchId}/deliveryterms`),
  getDeliveryMethodsForBranch: (branchId: number) =>
    httpClient.get<DeliveryMethod[]>(`branches/${branchId}/deliverymethods`),
  getCurrenciesForBranch: (branchId: number) =>
    httpClient.get<Currency[]>(`branches/${branchId}/currencies`),
};

export interface BranchSupplier {
  id: number;
  name: string;
  archived: boolean;
  isInternal: boolean;
  branchData: Array<{ branchId: number; supplierNumber: string | null }>;
}

export const branchTermsApi = {
  getAll: (kind: BranchTermKind, branchId: number) =>
    httpClient.get<Array<PaymentTerm | DeliveryTerm | DeliveryMethod>>(
      `branches/${branchId}/${branchTermEndpoints[kind]}`,
    ),
  create: (
    kind: BranchTermKind,
    branchId: number,
    request: BranchTermRequest,
  ) =>
    httpClient.post<PaymentTerm | DeliveryTerm | DeliveryMethod>(
      `branches/${branchId}/${branchTermEndpoints[kind]}`,
      { data: request },
    ),
  update: (kind: BranchTermKind, id: number, request: BranchTermRequest) =>
    httpClient.put<PaymentTerm | DeliveryTerm | DeliveryMethod>(
      `${branchTermEndpoints[kind]}/${id}`,
      { data: request },
    ),
};
